import {teamMemberKeysStore} from "@mytour/platform";
import type {TeamMember, UpdateWorkerPayload} from "@mytour/shared";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {teamWorkerApiService} from "./teamWorkerApi.service.js";

/**
 * Hook to create a new worker
 * ➕ Creates a new worker and invalidates related queries
 */
export function useCreateWorker() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: teamWorkerApiService.createWorker,
    onSuccess() {
      // Invalidate and refetch team members list
      queryClient.invalidateQueries({queryKey: teamMemberKeysStore.root});
    },
    onError(error) {
      console.error("Failed to create worker:", error);
    },
  });
  return {createWorker: mutateAsync, isCreatingWorker: isPending};
}

export interface UpdateWorkerVariables {
  workerId: string;
  payload: UpdateWorkerPayload;
}

/**
 * Hook to update a worker
 * ✏️ Updates an existing worker and invalidates related queries
 */
export function useUpdateWorker() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: ({workerId, payload}: UpdateWorkerVariables) =>
      teamWorkerApiService.updateWorker(workerId, payload),
    onSuccess(updatedWorker) {
      // Update the specific team member in the cache
      const members = queryClient.getQueryData(
        teamMemberKeysStore.allTeamMembers()
      ) as TeamMember[];
      const updatedMembers = members.map((member: TeamMember) => {
        if (member.id === updatedWorker.id) {
          return updatedWorker;
        }
        return member;
      });
      queryClient.setQueryData(teamMemberKeysStore.allTeamMembers(), updatedMembers);
    },
    onError(error) {
      console.error("Failed to update worker:", error);
    },
  });
  return {updateWorker: mutateAsync, isUpdatingWorker: isPending};
}

/**
 * Hook to activate a worker
 * ✅ Activates a worker and invalidates related queries
 */
export function useActivateWorker() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: teamWorkerApiService.activateWorker,
    onSuccess() {
      // Invalidate the team members list
      queryClient.invalidateQueries({
        queryKey: teamMemberKeysStore.allTeamMembers(),
      });
    },
    onError(error) {
      console.error("Failed to activate worker:", error);
    },
  });
  return {activateWorker: mutateAsync, isActivatingWorker: isPending};
}

/**
 * Hook to deactivate a worker
 * ❌ Deactivates a worker and invalidates related queries
 */
export function useDeactivateWorker() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: teamWorkerApiService.deactivateWorker,
    onSuccess() {
      /**
       * Invalidation totale, pas seulement teamMember : la désactivation
       * efface aussi le shiftingQuantity des RDV du membre côté serveur, et
       * ni `event` ni `billingMulti` (autres packages web) ne sont des
       * dépendances de company-settings pour cibler leurs query keys (#1038).
       */
      queryClient.invalidateQueries();
    },
    onError(error) {
      console.error("Failed to deactivate worker:", error);
    },
  });
  return {deactivateWorker: mutateAsync, isDeactivatingWorker: isPending};
}

/**
 * Hook to delete a worker
 * 🗑️ Soft deletes a worker and invalidates related queries
 */
export function useDeleteWorker() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending} = useMutation({
    mutationFn: teamWorkerApiService.deleteWorker,
    onSuccess() {
      // Invalidation totale (#1038) — même raison que useDeactivateWorker ci-dessus.
      queryClient.invalidateQueries();
    },
    onError(error) {
      console.error("Failed to delete worker:", error);
    },
  });
  return {deleteWorker: mutateAsync, isDeletingWorker: isPending};
}

/**
 * Hook to resend invitation to a worker
 * 📧 Resends invitation email to a worker
 */
export function useResendWorkerInvitation() {
  const {mutateAsync, isPending} = useMutation({
    mutationFn: teamWorkerApiService.resendInvitation,
    onSuccess() {
      // No cache invalidation needed for resend invitation
    },
    onError(error) {
      console.error("Failed to resend worker invitation:", error);
    },
  });
  return {resendWorkerInvitation: mutateAsync, isResendingWorkerInvitation: isPending};
}

/**
 * Hook to regenerate password for a worker
 * 🔐 Regenerates password and sends new credentials email to a worker
 */
export function useRegenerateWorkerPassword() {
  const {mutateAsync, isPending} = useMutation({
    mutationFn: teamWorkerApiService.regeneratePassword,
    onSuccess() {
      // No cache invalidation needed for password regeneration
    },
    onError(error) {
      console.error("Failed to regenerate worker password:", error);
    },
  });
  return {regenerateWorkerPassword: mutateAsync, isRegeneratingWorkerPassword: isPending};
}
